import clsx from "clsx";
import { useEffect, useState } from "react";

import { Icon } from "../../../assets/icons/Icon";
import { formatNumber, isRtl, pluralize } from "../../../helpers/helpers";
import { useElementWidth } from "../../../hooks/useElementWidth";
import MiddleTruncateTextPx from "../../dashboard/PageTable/MiddleTruncateUrl";
import FormLabel from "../../login/formLabel/FormLabel";
import Button from "../../mantine/button/Button";
import Switch from "../../switch/Switch";
import Modal from "../../ui/modal/Modal";
import Spinner from "../../ui/spinner/Spinner";
import TextArea from "../../ui/textArea/TextArea";
import styles from "../ContentItem.module.scss";
import { CategoryType } from "./OnPage";
import { ApprovePayload } from "./types";

interface AssetDetail {
  id?: number;
  suggestion?: string;
  original?: string;
  content?: string;
  approved?: boolean;
  active?: boolean;
  final?: string;
}

interface AssetItem {
  appearances: number;
  id: number;
  path_and_query: string;
  title?: string;
  path?: string;
  src?: string;
  suggestion?: string;
  active?: boolean;
  final?: string;
  details?: AssetDetail[];
  lang: string | null;
}

interface EditedSuggestion {
  final: string;
  active: boolean;
}

interface ContentSchemaModalProps {
  editedSuggestion: EditedSuggestion;
  setEditedSuggestion: React.Dispatch<React.SetStateAction<EditedSuggestion>>;
  lang: string | null;
}

interface CategoryAssetItemProps {
  item: AssetItem;
  approve: (data: ApprovePayload) => void;
  editPageSuggestion: (data: { id: number; final: string }) => void;
  isDefaultOpen: boolean;
  currentCategory: CategoryType;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasPendingStatus?: boolean;
  isWidgetActionLoading?: boolean;
}

const ContentSchemaModal = ({
  editedSuggestion,
  setEditedSuggestion,
  lang,
}: ContentSchemaModalProps) => {
  return (
    <div className={styles.editSuggestionContent}>
      <form>
        <FormLabel label="Suggestion" />
        <div
          className={clsx({ ["rtl"]: isRtl(lang) })}
          lang={lang || undefined}
        >
          <TextArea
            minRows={6}
            value={editedSuggestion.final}
            onChange={(e) =>
              setEditedSuggestion({
                ...editedSuggestion,
                final: e.target.value,
              })
            }
            placeholder="Edit Suggestion"
            style={{ width: "100%" }}
          />
        </div>
        <div className={styles.switchContainer}>
          <FormLabel label="Approve Changes " />
          <Switch
            checked={editedSuggestion.active}
            onChange={() => {
              const newActiveValue = !editedSuggestion.active;
              setEditedSuggestion((prev) => ({
                ...prev,
                active: newActiveValue,
              }));
            }}
          />
        </div>
      </form>
    </div>
  );
};

const CategoryAssetItem = ({
  item,
  approve,
  isDefaultOpen,
  isLoading,
  isDisabled = false,
  editPageSuggestion,
}: CategoryAssetItemProps) => {
  const [wrapperRef, wrapperWidth] = useElementWidth<HTMLDivElement>(300);
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const [lastClickedId, setLastClickedId] = useState<number | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editedSuggestion, setEditedSuggestion] = useState<EditedSuggestion>({
    final: "",
    active: false,
  });

  useEffect(() => {
    if (!isLoading) {
      setLastClickedId(null);
      setIsEditModalOpen(false);
    }
  }, [isLoading]);

  const toggleContent = () => {
    setIsOpen(!isOpen);
  };

  const handleApprove = (
    detail: AssetDetail | AssetItem,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setLastClickedId(detail.id ?? null);

    approve({
      kind: detail.active ? "unapprove" : "approve",
      ids: detail.id ? [detail.id] : undefined,
      variant: "automation",
    });
  };

  const editSuggestion = (event: React.MouseEvent) => {
    event.stopPropagation();

    setEditedSuggestion({
      final: item.final || item.suggestion || "",
      active: !!item.active,
    });

    setIsEditModalOpen(true);
  };

  const handleModalSubmit = () => {
    if (!item.id) return;

    setLastClickedId(item.id);

    if (editedSuggestion.active !== item.active) {
      approve({
        kind: editedSuggestion.active ? "approve" : "unapprove",
        ids: [item.id],
        variant: "automation",
      });
    }

    if (
      editedSuggestion.final !== item.final &&
      editedSuggestion.final !== item.suggestion
    ) {
      editPageSuggestion({
        id: item.id,
        final: editedSuggestion.final,
      });
    }
  };

  return (
    <>
      <div className={styles.currentItem}>
        <div className={styles.currentItemHeader} onClick={toggleContent}>
          <Icon
            iconName={isOpen ? "IconDown" : "ArrowRightIcon"}
            width="1rem"
            height="1rem"
          />
          <div className={styles.currentItemHeaderTitle} ref={wrapperRef}>
            {item.src && (
              <div className={styles.currentItemHeaderFlex}>
                <MiddleTruncateTextPx
                  text={item.src}
                  maxWidth={wrapperWidth - 160}
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                />
                {item?.appearances && (
                  <div className={styles.currentItemHeaderTitleAppearances}>
                    {formatNumber(item?.appearances)} (
                    {pluralize("appearance", item?.appearances)})
                  </div>
                )}
              </div>
            )}
            <div className={styles.currentItemContentHeaderIcons}>
              <Button
                variant="nohoverWithPadding"
                onClick={editSuggestion}
                disabled={isDisabled}
              >
                <Icon iconName="EditIcon" width="1rem" height="1rem" />
              </Button>
              <div className={styles.currentItemHeaderIcons}>
                {isLoading && lastClickedId === item.id ? (
                  <Spinner size="1rem" />
                ) : (
                  <Button
                    disabled={isDisabled}
                    variant="nohoverWithPadding"
                    onClick={(event) => handleApprove(item, event)}
                  >
                    <Icon
                      iconName={
                        item.active ? "GreenCheckmarkIcon" : "CloseIconCircled"
                      }
                      width="1rem"
                      height="1rem"
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {isOpen && (
          <div className={styles.currentItemContent}>
            <div>
              <div className={styles.currentItemContentTitle}>Suggestion</div>
              <div
                className={clsx(styles.currentItemContentSuggestion, {
                  ["rtl"]: isRtl(item?.lang) && item?.suggestion,
                })}
                lang={item?.lang || undefined}
              >
                {item?.final || item?.suggestion || "Blank"}
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal
        isLoading={isLoading}
        isOpen={isEditModalOpen}
        closeModal={() => setIsEditModalOpen(false)}
        buttonLabel="Update"
        formId="editSuggestion"
        // @ts-expect-error TS2339 non-existing className
        modalLabel={<h3 className={styles.modalTitle}>Edit Suggestion</h3>}
        action={handleModalSubmit}
      >
        <ContentSchemaModal
          editedSuggestion={editedSuggestion}
          setEditedSuggestion={setEditedSuggestion}
          lang={item?.lang}
        />
      </Modal>
    </>
  );
};

export default CategoryAssetItem;
